import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { authApi } from '../api/authApi'
import { supabaseAuth, NoSupabaseAccountError, SupabaseIdentityUnknownError } from '../api/supabaseAuth'
import { useAuthStore } from '../stores/authStore'
import type { LoginCredentials, RegisterData } from '../types'

export const useAuth = () => {
  const { user, isHydrated, setUserSync, clearUser } = useAuthStore()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  // Note: We don't validate session on mount because the axios interceptor
  // will handle 401 errors and redirect to login automatically
  // This prevents unnecessary API calls and avoids race conditions

  /**
   * One form, either issuer.
   *
   * Supabase is the identity provider, but 1262 dormant artists still have only a legacy AMS
   * password. Rather than ask a person which kind of account they have — a question about our
   * migration, not about them — try Supabase and fall back when there is no account there.
   * Whoever they are, the same screen works, and the token from either path goes into the same
   * store, so every API call downstream is unchanged.
   */
  const signInMutation = useMutation({
    mutationFn: async (credentials: LoginCredentials) => {
      if (supabaseAuth.isConfigured()) {
        try {
          const { user, accessToken } = await supabaseAuth.signIn(
            credentials.email,
            credentials.password,
          )
          useAuthStore.getState().setToken(accessToken)
          return user
        } catch (error) {
          // ANY Supabase failure falls through to the legacy login.
          //
          // The earlier version rethrew everything except "no account here", which made a working
          // legacy login depend on a service that is not the source of truth for those users: a
          // Supabase outage, a rate limit, or a network blip would have failed every sign-in on the
          // platform, including the 1262 artists Supabase knows nothing about. Falling through
          // costs one wasted round trip on a genuinely bad password; not falling through costs
          // everyone their login.
          //
          // The one thing NOT swallowed is a failure after Supabase authenticated successfully —
          // that means the identity is real but maps to no artist here, and retrying it as a legacy
          // password would report "wrong password" for what is actually a missing account link.
          if (error instanceof SupabaseIdentityUnknownError) throw error
          if (!(error instanceof NoSupabaseAccountError)) {
            console.warn('[auth] supabase sign-in unavailable, falling back to platform login', error)
          }
        }
      }
      return authApi.signIn(credentials)
    },
    onSuccess: async (user) => {
      // Wait for zustand persist to complete writing to localStorage
      await setUserSync(user)
      navigate('/app')
    },
  })

  const signOutMutation = useMutation({
    mutationFn: authApi.signOut,
    onSuccess: () => {
      clearUser()
      queryClient.clear()
      navigate('/')
    },
  })

  const registerMutation = useMutation({
    mutationFn: authApi.register,
    // Registration success is now handled by the SignUp component
    // to show a success screen before redirecting
  })

  // Helper to register and return a promise for component-level handling
  const registerAsync = async (data: RegisterData) => {
    return registerMutation.mutateAsync(data)
  }

  return {
    user,
    isLoading: !isHydrated, // Show loading until store is hydrated from localStorage
    isAuthenticated: !!user,
    signIn: signInMutation.mutate,
    signOut: signOutMutation.mutate,
    register: registerAsync,
    isSigningIn: signInMutation.isPending,
    isSigningOut: signOutMutation.isPending,
    isRegistering: registerMutation.isPending,
    signInError: signInMutation.error,
    registerError: registerMutation.error,
  }
}

